// ============================================
// 预设动画库
// ============================================

import { KeyframeEngine, createDefaultClip, createTrack, createKeyframe } from './KeyframeEngine';
import type { AnimationClip, InterpolationType } from './KeyframeEngine';

export interface AnimationPreset {
  name: string;
  description: string;
  category: 'transform' | 'camera' | 'effect';
  icon: string;
  duration: number;
  create: (targetId: string, options?: any) => AnimationClip;
}

function uuid(): string {
  return crypto.randomUUID();
}

// ============================================
// 变换动画预设
// ============================================

const rotateY: AnimationPreset = {
  name: 'Y轴旋转',
  description: '绕Y轴360度持续旋转',
  category: 'transform',
  icon: 'RotateRightOutlined',
  duration: 4,
  create: (targetId) => ({
    ...createDefaultClip('Y轴旋转', 4),
    loop: true,
    tracks: [
      createTrack('旋转', targetId, 'rotation.y', [
        createKeyframe(0, [0]),
        createKeyframe(1, [Math.PI * 2]),
      ]),
    ],
  }),
};

const pulse: AnimationPreset = {
  name: '呼吸缩放',
  description: '在1.0-1.2倍之间循环缩放',
  category: 'transform',
  icon: 'ExpandOutlined',
  duration: 2,
  create: (targetId) => ({
    ...createDefaultClip('呼吸缩放', 2),
    loop: true,
    tracks: [
      createTrack('缩放X', targetId, 'scale.x', [
        createKeyframe(0, [1], 'easeInOut'),
        createKeyframe(0.5, [1.2], 'easeInOut'),
        createKeyframe(1, [1], 'easeInOut'),
      ]),
      createTrack('缩放Y', targetId, 'scale.y', [
        createKeyframe(0, [1], 'easeInOut'),
        createKeyframe(0.5, [1.2], 'easeInOut'),
        createKeyframe(1, [1], 'easeInOut'),
      ]),
      createTrack('缩放Z', targetId, 'scale.z', [
        createKeyframe(0, [1], 'easeInOut'),
        createKeyframe(0.5, [1.2], 'easeInOut'),
        createKeyframe(1, [1], 'easeInOut'),
      ]),
    ],
  }),
};

const bounce: AnimationPreset = {
  name: '弹跳',
  description: '上下弹跳效果',
  category: 'transform',
  icon: 'ArrowUpOutlined',
  duration: 1.5,
  create: (targetId) => ({
    ...createDefaultClip('弹跳', 1.5),
    loop: true,
    tracks: [
      createTrack('位置Y', targetId, 'position.y', [
        createKeyframe(0, [0], 'easeOut'),
        createKeyframe(0.3, [3], 'easeIn'),
        createKeyframe(0.5, [0], 'easeOut'),
        createKeyframe(0.65, [1.5], 'easeIn'),
        createKeyframe(0.8, [0], 'easeOut'),
        createKeyframe(0.9, [0.5], 'easeIn'),
        createKeyframe(1, [0], 'easeOut'),
      ]),
    ],
  }),
};

const swing: AnimationPreset = {
  name: '摆动',
  description: '左右摆动效果',
  category: 'transform',
  icon: 'SwapOutlined',
  duration: 3,
  create: (targetId) => ({
    ...createDefaultClip('摆动', 3),
    loop: true,
    tracks: [
      createTrack('旋转Z', targetId, 'rotation.z', [
        createKeyframe(0, [0], 'easeInOut'),
        createKeyframe(0.25, [0.3], 'easeInOut'),
        createKeyframe(0.5, [0], 'easeInOut'),
        createKeyframe(0.75, [-0.3], 'easeInOut'),
        createKeyframe(1, [0], 'easeInOut'),
      ]),
    ],
  }),
};

const fadeInOut: AnimationPreset = {
  name: '淡入淡出',
  description: '透明度从0到1再到0',
  category: 'effect',
  icon: 'EyeOutlined',
  duration: 4,
  create: (targetId) => ({
    ...createDefaultClip('淡入淡出', 4),
    tracks: [
      createTrack('透明度', targetId, 'opacity', [
        createKeyframe(0, [0], 'easeInOut'),
        createKeyframe(0.3, [1], 'easeInOut'),
        createKeyframe(0.7, [1], 'easeInOut'),
        createKeyframe(1, [0], 'easeInOut'),
      ]),
    ],
  }),
};

const flyIn: AnimationPreset = {
  name: '飞入',
  description: '从远处飞入到当前位置',
  category: 'transform',
  icon: 'VerticalRightOutlined',
  duration: 2,
  create: (targetId, options?: { from?: [number, number, number] }) => ({
    ...createDefaultClip('飞入', 2),
    tracks: [
      createTrack('位置X', targetId, 'position.x', [
        createKeyframe(0, [options?.from?.[0] ?? 20], 'easeOut'),
        createKeyframe(1, [0], 'easeOut'),
      ]),
      createTrack('位置Y', targetId, 'position.y', [
        createKeyframe(0, [options?.from?.[1] ?? 15], 'easeOut'),
        createKeyframe(1, [0], 'easeOut'),
      ]),
      createTrack('位置Z', targetId, 'position.z', [
        createKeyframe(0, [options?.from?.[2] ?? 20], 'easeOut'),
        createKeyframe(1, [0], 'easeOut'),
      ]),
    ],
  }),
};

const explosion: AnimationPreset = {
  name: '爆炸分解',
  description: '模型爆炸分解效果',
  category: 'effect',
  icon: 'FireOutlined',
  duration: 3,
  create: (targetId, options?: { spread?: number }) => {
    const spread = options?.spread ?? 5;
    return {
      ...createDefaultClip('爆炸分解', 3),
      tracks: [
        createTrack('位置X', targetId, 'position.x', [
          createKeyframe(0, [0]),
          createKeyframe(0.5, [spread], 'easeOut'),
          createKeyframe(1, [0], 'easeInOut'),
        ]),
        createTrack('位置Y', targetId, 'position.y', [
          createKeyframe(0, [0]),
          createKeyframe(0.5, [spread * 1.5], 'easeOut'),
          createKeyframe(1, [0], 'easeInOut'),
        ]),
        createTrack('位置Z', targetId, 'position.z', [
          createKeyframe(0, [0]),
          createKeyframe(0.5, [spread], 'easeOut'),
          createKeyframe(1, [0], 'easeInOut'),
        ]),
        createTrack('旋转X', targetId, 'rotation.x', [
          createKeyframe(0, [0]),
          createKeyframe(1, [Math.PI * 2], 'easeInOut'),
        ]),
        createTrack('缩放', targetId, 'scale.x', [
          createKeyframe(0, [1]),
          createKeyframe(0.5, [0.5], 'easeOut'),
          createKeyframe(1, [1], 'easeInOut'),
        ]),
      ],
    };
  },
};

const layerReveal: AnimationPreset = {
  name: '分层展示',
  description: '逐层升起展示',
  category: 'transform',
  icon: 'LayerOutlined',
  duration: 5,
  create: (targetId, options?: { layers?: number }) => {
    const layers = options?.layers ?? 3;
    const tracks = [];
    for (let i = 0; i < layers; i++) {
      const delay = i / layers;
      const track = createTrack(`层${i + 1}位置Y`, targetId, `position.y`, [
        createKeyframe(0, [0]),
        createKeyframe(delay, [0]),
        createKeyframe(delay + 0.3, [i * 2], 'easeOut'),
        createKeyframe(1, [i * 2]),
      ]);
      tracks.push(track);
    }
    return {
      ...createDefaultClip('分层展示', 5),
      tracks,
    };
  },
};

// ============================================
// 相机动画预设
// ============================================

const cameraOrbit: AnimationPreset = {
  name: '环绕旋转',
  description: '相机围绕目标旋转',
  category: 'camera',
  icon: 'SyncOutlined',
  duration: 8,
  create: (_targetId, options?: { radius?: number; height?: number }) => {
    const radius = options?.radius ?? 15;
    const height = options?.height ?? 8;
    const segments = 64;
    const positionKeyframes: any[] = [];
    const targetKeyframes: any[] = [];

    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const angle = t * Math.PI * 2;
      positionKeyframes.push(
        createKeyframe(t, [
          Math.cos(angle) * radius,
          height,
          Math.sin(angle) * radius,
        ])
      );
      targetKeyframes.push(createKeyframe(t, [0, 0, 0]));
    }

    return {
      ...createDefaultClip('环绕旋转', 8),
      loop: true,
      tracks: [
        createTrack('相机位置', 'camera', 'position', positionKeyframes),
        createTrack('相机目标', 'camera', 'target', targetKeyframes),
      ],
    };
  },
};

const cameraShake: AnimationPreset = {
  name: '相机抖动',
  description: '相机震动效果',
  category: 'camera',
  icon: 'AlertOutlined',
  duration: 2,
  create: (_targetId, options?: { intensity?: number }) => {
    const intensity = options?.intensity ?? 0.5;
    return {
      ...createDefaultClip('相机抖动', 2),
      tracks: [
        createTrack('抖动X', 'camera', 'position.x', [
          createKeyframe(0, [0]),
          createKeyframe(0.1, [intensity]),
          createKeyframe(0.2, [-intensity]),
          createKeyframe(0.3, [intensity * 0.7]),
          createKeyframe(0.4, [-intensity * 0.7]),
          createKeyframe(0.5, [intensity * 0.5]),
          createKeyframe(0.6, [-intensity * 0.5]),
          createKeyframe(0.7, [intensity * 0.3]),
          createKeyframe(0.8, [-intensity * 0.3]),
          createKeyframe(1, [0]),
        ]),
      ],
    };
  },
};

const cameraZoom: AnimationPreset = {
  name: '相机推拉',
  description: '相机推近/拉远',
  category: 'camera',
  icon: 'ZoomInOutlined',
  duration: 4,
  create: (_targetId, options?: { from?: number; to?: number }) => {
    const from = options?.from ?? 25;
    const to = options?.to ?? 8;
    return {
      ...createDefaultClip('相机推拉', 4),
      tracks: [
        createTrack('推拉Z', 'camera', 'position.z', [
          createKeyframe(0, [from], 'easeInOut'),
          createKeyframe(0.5, [to], 'easeInOut'),
          createKeyframe(1, [from], 'easeInOut'),
        ]),
      ],
    };
  },
};

// ============================================
// 导出预设列表
// ============================================

export const animationPresets: AnimationPreset[] = [
  rotateY,
  pulse,
  bounce,
  swing,
  fadeInOut,
  flyIn,
  explosion,
  layerReveal,
  cameraOrbit,
  cameraShake,
  cameraZoom,
];

export const transformPresets = animationPresets.filter(p => p.category === 'transform');
export const cameraPresets = animationPresets.filter(p => p.category === 'camera');
export const effectPresets = animationPresets.filter(p => p.category === 'effect');

export function getPresetByName(name: string): AnimationPreset | undefined {
  return animationPresets.find(p => p.name === name);
}

export function getPresetByCategory(category: AnimationPreset['category']): AnimationPreset[] {
  return animationPresets.filter(p => p.category === category);
}
